<template>
  <v-dialog
    :value="visible"
    max-width="900"
    content-class="sc-modal sc-modal--lg"
    @input="$emit('close')"
    scrollable
  >
    <v-card v-if="visible" flat class="sc-modal__card">
      <v-btn icon class="sc-modal__close" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <!-- Header -->
      <div class="sc-modal__header">
        <v-icon color="primary" class="mr-3">mdi-image-search-outline</v-icon>
        <div>
          <span class="sc-modal__title">Selecionar imagem</span>
          <span class="sc-modal__subtitle">Biblioteca da produção ou busca no Graphic Hub</span>
        </div>
      </div>

      <!-- Tabs -->
      <v-tabs v-model="activeTab" background-color="transparent" class="picker-tabs">
        <v-tab>
          <v-icon small class="mr-1">mdi-image-multiple</v-icon>
          Biblioteca
          <v-badge
            v-if="filteredLibraryImages.length > 0"
            :content="filteredLibraryImages.length"
            inline
            color="primary"
            class="ml-2"
          />
        </v-tab>
        <v-tab>
          <v-icon small class="mr-1">mdi-folder-search-outline</v-icon>
          Buscar no Graphic Hub
        </v-tab>
      </v-tabs>

      <!-- Body -->
      <div class="sc-modal__body sc-modal__body--flush">
        <v-tabs-items v-model="activeTab">
          <!-- ==================== Tab 1: Biblioteca ==================== -->
          <v-tab-item>
            <!-- Toolbar: filter + upload in one row -->
            <div class="picker-toolbar">
              <v-text-field
                v-model="librarySearch"
                placeholder="Filtrar por nome..."
                solo
                flat
                dense
                background-color="surface_tertiary"
                hide-details
                clearable
                prepend-inner-icon="mdi-filter-outline"
                @click:clear="librarySearch = ''"
                class="toolbar-search mr-3"
              />

              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="onFileSelected"
              />
              <v-btn
                small
                color="primary"
                @click="$refs.fileInput.click()"
                class="toolbar-btn"
              >
                <v-icon left small>mdi-upload</v-icon>
                Enviar imagem
              </v-btn>
            </div>

            <!-- Pending upload banner -->
            <transition name="slide-banner">
              <div v-if="uploadFile" class="upload-banner">
                <v-icon small class="mr-2" color="primary">mdi-file-image-outline</v-icon>
                <span class="upload-banner-name text-truncate">{{ uploadFile.name }}</span>
                <span class="upload-banner-size ml-1">
                  ({{ formatFileSize(uploadFile.size) }})
                </span>
                <v-spacer />
                <v-btn
                  small
                  text
                  @click="clearUploadFile"
                  class="banner-btn-cancel"
                >
                  <v-icon left small>mdi-close</v-icon>
                  Cancelar
                </v-btn>
                <v-btn
                  small
                  color="primary"
                  @click="handleUpload"
                  :loading="uploading"
                  class="banner-btn-send"
                >
                  <v-icon left small>mdi-cloud-upload-outline</v-icon>
                  Enviar
                </v-btn>
              </div>
            </transition>

            <!-- Image grid -->
            <div class="picker-grid">
              <div v-if="productionImagesLoading" class="d-flex justify-center pa-8">
                <v-progress-circular indeterminate color="primary" />
              </div>

              <div v-else-if="filteredLibraryImages.length > 0">
                <v-row dense>
                  <v-col
                    v-for="img in filteredLibraryImages"
                    :key="img.id_gh_imagem"
                    cols="6"
                    md="4"
                    lg="3"
                  >
                    <div
                      class="image-card"
                      :class="{ 'image-card--selected': isSelected(img) }"
                      @click="selectImage(img)"
                    >
                      <div class="image-card-thumb">
                        <v-img
                          v-if="img.signedUrl"
                          :src="img.signedUrl"
                          height="110"
                          contain
                        >
                          <template v-slot:placeholder>
                            <div class="thumb-placeholder">
                              <v-progress-circular indeterminate size="20" width="2" color="primary" />
                            </div>
                          </template>
                        </v-img>
                        <div v-else class="thumb-placeholder">
                          <v-icon color="text_secondary">mdi-image-outline</v-icon>
                        </div>

                        <!-- Selected check -->
                        <v-icon
                          v-if="isSelected(img)"
                          class="selected-badge"
                          color="primary"
                          size="20"
                        >mdi-check-circle</v-icon>

                        <!-- Origin badge -->
                        <span
                          class="image-card__badge sc-badge"
                          :class="img.dsc_origem === 'graphic_hub' ? 'sc-badge--info' : 'sc-badge--primary'"
                        >
                          {{ img.dsc_origem === 'graphic_hub' ? 'GH' : 'UP' }}
                        </span>
                      </div>

                      <div class="image-card-info">
                        <span class="image-card-name text-truncate">{{ img.dsc_nome_imagem }}</span>
                        <v-btn
                          x-small
                          icon
                          @click.stop="handleDeleteImage(img)"
                          title="Excluir"
                          class="image-card-delete"
                        >
                          <v-icon x-small>mdi-delete-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <div v-else class="sc-empty-state">
                <v-icon size="48" color="text_secondary" class="mb-3">mdi-image-plus</v-icon>
                <div class="sc-empty-state__title">Nenhuma imagem na biblioteca</div>
                <div class="sc-empty-state__subtitle">
                  Clique em <strong>Enviar imagem</strong> ou importe do
                  <strong>Graphic Hub</strong>
                </div>
              </div>
            </div>
          </v-tab-item>

          <!-- ==================== Tab 2: Buscar no Graphic Hub ==================== -->
          <v-tab-item>
            <!-- Search toolbar -->
            <div class="picker-toolbar">
              <v-text-field
                v-model="ghSearchTerm"
                placeholder="Buscar imagens no Graphic Hub..."
                solo
                flat
                dense
                background-color="surface_tertiary"
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                @keyup.enter="handleGhSearch"
                @click:clear="ghSearchTerm = ''"
                class="toolbar-search"
              />
              <v-select
                v-model="ghCategory"
                :items="ghCategories"
                placeholder="Tipo"
                solo
                flat
                dense
                background-color="surface_tertiary"
                hide-details
                clearable
                class="gh-type-select ml-3"
              />
              <v-btn
                small
                color="primary"
                @click="handleGhSearch"
                :disabled="!ghSearchTerm || ghSearchLoading"
                :loading="ghSearchLoading"
                class="ml-3 toolbar-btn"
              >
                Buscar
              </v-btn>
            </div>

            <!-- GH results grid -->
            <div class="picker-grid">
              <div v-if="ghSearchLoading" class="d-flex justify-center pa-8">
                <v-progress-circular indeterminate color="primary" />
              </div>

              <div v-else-if="ghSearchResults && ghSearchResults.entries && ghSearchResults.entries.length > 0">
                <div class="gh-results-count mb-2">
                  {{ ghSearchResults.entries.length }} de {{ ghSearchResults.totalResults }} resultados
                </div>
                <v-row dense>
                  <v-col
                    v-for="file in ghSearchResults.entries"
                    :key="file.uuid"
                    cols="6"
                    md="4"
                    lg="3"
                  >
                    <div class="gh-card">
                      <div class="gh-card-thumb">
                        <v-img
                          v-if="file.thumbnailUrl"
                          :src="file.thumbnailUrl"
                          height="100"
                          contain
                        >
                          <template v-slot:placeholder>
                            <div class="thumb-placeholder" style="height: 100px">
                              <v-progress-circular indeterminate size="20" width="2" color="primary" />
                            </div>
                          </template>
                        </v-img>
                        <div v-else class="thumb-placeholder" style="height: 100px">
                          <v-icon color="text_secondary">mdi-image-outline</v-icon>
                        </div>

                        <!-- Type badge -->
                        <span v-if="file.type" class="image-card__badge sc-badge sc-badge--info">
                          {{ file.type }}
                        </span>
                      </div>

                      <div class="gh-card-info">
                        <span class="gh-card-name text-truncate">{{ file.title }}</span>
                        <v-btn
                          x-small
                          color="primary"
                          outlined
                          @click="handleImportGhFile(file)"
                          :loading="importingUuids[file.uuid]"
                          :disabled="!!importingUuids[file.uuid]"
                          class="gh-card-import"
                        >
                          <v-icon x-small left>mdi-download</v-icon>
                          Importar
                        </v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <div v-else class="sc-empty-state">
                <v-icon size="48" color="text_secondary" class="mb-3">mdi-folder-search-outline</v-icon>
                <div class="sc-empty-state__title">Buscar no Graphic Hub</div>
                <div class="sc-empty-state__subtitle">
                  Pesquise por nome e importe imagens diretamente
                </div>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </div>

      <!-- Footer with selected preview -->
      <div class="sc-modal__footer">
        <div v-if="selectedImage" class="picker-selected">
          <v-img
            :src="selectedImage.signedUrl"
            width="40"
            height="40"
            contain
            class="picker-selected__thumb mr-2"
          />
          <span class="picker-selected__name text-truncate">
            {{ selectedImage.dsc_nome_imagem }}
          </span>
        </div>
        <div v-else class="picker-selected__hint">
          Clique em uma imagem para selecionar
        </div>
        <v-btn text @click="$emit('close')" class="modal-btn-cancel">Cancelar</v-btn>
        <v-btn
          color="primary"
          :disabled="!selectedImage"
          @click="confirmSelection"
          class="modal-btn-confirm"
        >
          Selecionar
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { apiClient } from "@/services/api";

export default {
  name: "GloboGraphicsImagePicker",
  props: {
    visible: { type: Boolean, default: false },
    fieldName: { type: String, default: null },
    cod_producao: { type: Number, required: true },
  },
  data() {
    return {
      activeTab: 0,
      uploadFile: null,
      uploading: false,
      librarySearch: "",
      selectedImage: null,
      ghSearchTerm: "",
      ghCategory: null,
      ghSearchLoading: false,
      ghSearchResults: null,
      importingUuids: {},
      ghCategories: [
        { text: "Todos", value: null },
        { text: "Image", value: "IMAGE" },
        { text: "Scene", value: "SCENE" },
        { text: "Video", value: "VIDEO_CLIP" },
        { text: "Audio", value: "AUDIO_CLIP" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      productionImages: "globographics/getterProductionImages",
      productionImagesLoading: "globographics/getterProductionImagesLoading",
    }),
    filteredLibraryImages() {
      if (!this.librarySearch) return this.productionImages;
      const term = this.librarySearch.toLowerCase();
      return this.productionImages.filter(img =>
        (img.dsc_nome_imagem || "").toLowerCase().includes(term)
      );
    },
  },
  created() {
    this.fetchProductionImages({ codProducao: this.cod_producao });
  },
  methods: {
    ...mapActions({
      fetchProductionImages: "globographics/fetchProductionImages",
      storeUploadImage: "globographics/uploadProductionImage",
      storeImportGhImage: "globographics/importGhImage",
      storeDeleteImage: "globographics/deleteProductionImage",
    }),
    isSelected(img) {
      return this.selectedImage && this.selectedImage.id_gh_imagem === img.id_gh_imagem;
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    selectImage(img) {
      this.selectedImage = img;
    },
    confirmSelection() {
      if (!this.selectedImage) return;
      this.$emit("select", {
        fieldName: this.fieldName,
        imageUrl: this.selectedImage.signedUrl,
      });
      this.$emit("close");
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      this.uploadFile = file || null;
    },
    clearUploadFile() {
      this.uploadFile = null;
      this.$refs.fileInput.value = '';
    },
    async handleUpload() {
      if (!this.uploadFile) return;
      this.uploading = true;
      try {
        await this.storeUploadImage({
          codProducao: this.cod_producao,
          file: this.uploadFile,
        });
        this.uploadFile = null;
        this.$refs.fileInput.value = '';
      } catch (error) {
        const msg = error.response?.data?.error || "Erro ao fazer upload";
        this.$toast.error(msg);
      } finally {
        this.uploading = false;
      }
    },
    async handleDeleteImage(img) {
      try {
        await this.storeDeleteImage({
          idGhImagem: img.id_gh_imagem,
          codProducao: this.cod_producao,
        });
        if (this.selectedImage?.id_gh_imagem === img.id_gh_imagem) {
          this.selectedImage = null;
        }
      } catch (error) {
        const msg = error.response?.data?.error || "Erro ao excluir imagem";
        this.$toast.error(msg);
      }
    },
    async handleGhSearch() {
      if (!this.ghSearchTerm) return;
      this.ghSearchLoading = true;
      try {
        // Use a direct API call instead of the store action to avoid
        // overwriting the shared searchResults state used by other components.
        const params = new URLSearchParams({ q: this.ghSearchTerm });
        if (this.ghCategory) params.append("category", this.ghCategory);
        params.append("count", 50);
        const { data } = await apiClient.get(`globographics/search?${params}`);
        this.ghSearchResults = data;
      } catch (error) {
        this.$toast.error("Erro ao buscar no Graphic Hub");
      } finally {
        this.ghSearchLoading = false;
      }
    },
    async handleImportGhFile(file) {
      this.$set(this.importingUuids, file.uuid, true);
      try {
        await this.storeImportGhImage({
          codProducao: this.cod_producao,
          fileUuid: file.uuid,
          fileName: file.title || "imported_image",
          mimeType: file.type === "IMAGE" ? "image/png" : null,
        });
        this.activeTab = 0;
      } catch (error) {
        const msg = error.response?.data?.error || "Erro ao importar imagem";
        this.$toast.error(msg);
      } finally {
        this.$set(this.importingUuids, file.uuid, false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Structure comes from _modal.scss (sc-modal__*). The body is flush so the
// tabbed browser (toolbar + upload banner + image grid) sits edge-to-edge and
// owns its own inner padding.
.sc-modal__body--flush {
  padding: 0;
}

// ---- Tabs ----
.picker-tabs {
  flex-shrink: 0;
  padding: 0 var(--sc-space-6);
  border-bottom: 1px solid var(--sc-border-subtle);

  ::v-deep .v-tab {
    text-transform: none;
    letter-spacing: 0;
    font-weight: var(--sc-weight-medium);
    color: var(--v-text_primary-base) !important;
  }
  ::v-deep .v-tab--active {
    color: var(--v-primary-base) !important;
  }
}

::v-deep .v-tabs-items {
  background: transparent !important;
}

// ---- Shared toolbar ----
.picker-toolbar {
  display: flex;
  align-items: center;
  padding: var(--sc-space-3) var(--sc-space-4);
  border-bottom: 1px solid var(--sc-border-subtle);
}

.toolbar-btn {
  flex-shrink: 0;
  text-transform: none;
  letter-spacing: 0;
  height: 40px !important;
}

.toolbar-search {
  flex: 1;
}

.gh-type-select {
  flex: none;
  width: 130px;
}

// ---- Upload banner ----
.upload-banner {
  display: flex;
  align-items: center;
  padding: var(--sc-space-3) var(--sc-space-4);
  margin: var(--sc-space-3) var(--sc-space-4);
  border-radius: var(--sc-radius-sm);
  background: rgba(var(--sc-color-primary-rgb), 0.08);
  border: 1px solid rgba(var(--sc-color-primary-rgb), 0.25);
}

.banner-btn-cancel {
  text-transform: none;
  letter-spacing: 0;
  color: var(--v-text_secondary-base) !important;
  margin-right: var(--sc-space-2);
}

.banner-btn-send {
  text-transform: none;
  letter-spacing: 0;
}

.upload-banner-name {
  font-size: var(--sc-text-base);
  font-weight: var(--sc-weight-medium);
  color: var(--v-text_primary-base);
  max-width: 280px;
}

.upload-banner-size {
  font-size: var(--sc-text-md);
  color: var(--v-text_secondary-base);
}

.slide-banner-enter-active,
.slide-banner-leave-active {
  transition: all var(--sc-duration-base) var(--sc-easing-standard);
}
.slide-banner-enter,
.slide-banner-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 0;
}

// ---- Grid body ----
// No max-height/scroll here — the bounded .sc-modal__body owns the scroll.
.picker-grid {
  min-height: 200px;
  padding: var(--sc-space-3);
}

// ---- Library image card ----
.image-card {
  position: relative;
  border-radius: var(--sc-radius-md);
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  background: var(--sc-surface-tint);
  transition: all var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    border-color: var(--sc-border-hover);
    background: var(--sc-surface-tint-hover);
  }

  &--selected {
    border-color: var(--v-primary-base) !important;
    box-shadow: 0 0 0 1px var(--v-primary-base);
  }
}

.image-card-thumb {
  position: relative;
  background: rgba(0, 0, 0, 0.15);
}

.image-card-info {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  min-height: 32px;
}

.image-card-name {
  flex: 1;
  font-size: var(--sc-text-md);
  color: var(--v-text_primary-base);
}

.image-card-delete {
  flex-shrink: 0;
  opacity: 0.4;
  transition: opacity var(--sc-duration-fast), color var(--sc-duration-fast);

  .image-card:hover & {
    opacity: 1;
  }

  &:hover {
    color: var(--v-error-base, #ff5252) !important;
    opacity: 1 !important;
  }

  &:hover .v-icon {
    color: var(--v-error-base, #ff5252) !important;
  }
}

// ---- Selected check icon ----
.selected-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
}

// ---- Card origin/type badge — positions the shared .sc-badge on the thumb ----
.image-card__badge {
  position: absolute;
  bottom: 4px;
  left: 4px;
}

// ---- GH search cards ----
.gh-results-count {
  font-size: var(--sc-text-md);
  color: var(--v-text_secondary-base);
}

.gh-card {
  border-radius: var(--sc-radius-md);
  overflow: hidden;
  border: 1px solid var(--sc-border-subtle);
  background: var(--sc-surface-tint);
  transition: border-color var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    border-color: var(--sc-border-hover);
  }
}

.gh-card-thumb {
  position: relative;
  background: rgba(0, 0, 0, 0.2);
}

.gh-card-info {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  gap: 8px;
}

.gh-card-name {
  flex: 1;
  font-size: var(--sc-text-md);
  font-weight: var(--sc-weight-medium);
  color: var(--v-text_primary-base);
}

.gh-card-import {
  flex-shrink: 0;
  text-transform: none;
  letter-spacing: 0;
}

// ---- Thumbnail placeholder ----
.thumb-placeholder {
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.08);
}

// ---- Footer selected-image preview (pushed to the left of the buttons) ----
.picker-selected {
  display: flex;
  align-items: center;
  overflow: hidden;
  max-width: 300px;
  margin-right: auto;
}

.picker-selected__thumb {
  flex-shrink: 0;
  border-radius: var(--sc-radius-sm);
  background: rgba(0, 0, 0, 0.1);
}

.picker-selected__name {
  font-size: var(--sc-text-lg);
  font-weight: var(--sc-weight-medium);
  color: var(--v-text_primary-base);
}

.picker-selected__hint {
  margin-right: auto;
  align-self: center;
  font-size: var(--sc-text-lg);
  color: var(--v-text_secondary-base);
  font-style: italic;
}

// ---- Global overrides for dark inputs ----
::v-deep label,
::v-deep input::placeholder {
  color: var(--v-text_primary-base) !important;
}

::v-deep .v-chip__content {
  color: var(--v-text_primary-base);
}
</style>
